/**
 * menueFarbe — die Farbe EINES Menüeintrags, aus dem Ton des Themes.
 *
 * @description
 *   Ein Menüeintrag darf `color` tragen (`{ …, color: 'warning' }`). Ein nackter
 *   Palettenschlüssel ist aber KEIN CSS-Wert: in `sx` löst `color` gegen
 *   `theme.palette` auf, `'warning'` also gegen das PALETTENOBJEKT
 *   `{ main, light, dark, contrastText }` — die Deklaration ist damit ungültig
 *   und die Farbe kommt nie an. Diese Funktion macht daraus einen tragfähigen
 *   Wert.
 *
 *   🔴 Sie zielt auf den **Textton** des Tons, nicht auf `<schlüssel>.main`.
 *   Gemessen (WCAG 2.x, sRGB, gegen `background.paper` des eigenen Modus):
 *   `warning.main` erreicht auf hellem Papier **3,113:1**, `info.main`
 *   **3,857:1** — beide unter der Schwelle 4,5. Der Weg über die v2-Tonskala
 *   liegt in **beiden** Modi darüber (warn 5,022 / 8,763 · err 6,470 / 7,708 ·
 *   ok 5,016 / 8,395 · info 5,169 / 5,754 · agent 5,699 / 7,924 · brand
 *   10,638 / 8,112). Angefasst wird dabei **kein** Wert in `tokens.js` oder
 *   `theme.js` — die Skala existiert, ist modusaufgelöst und war für den
 *   dunklen Modus bereits gegengeprüft; neu ist allein, dass diese Abbildung
 *   sie **benutzt**. Die Papierwerte und die Hexe der Skala stehen dort, wo sie
 *   hingehören (`tokens.js`, `theme.js`) und werden hier nicht wiederholt —
 *   Regel 22 liest auch Kommentare.
 *
 *   🔴 EIN Modul für BEIDE Aufrufer. Bis zum 2026-10-08 lag dieselbe Abbildung
 *   zweimal im Baum — als `ueberlaufFarbe` in `ViewHeader.jsx` und als
 *   `menueFarbe` in `SplitButton.jsx` —, begründet mit einem Zyklus: `ViewHeader`
 *   importiert `SplitButton`, ein Import in die Gegenrichtung wäre einer.
 *   Gemessen ist die Kante einbahnig `ViewHeader → SplitButton`, und ein
 *   **drittes** Modul, das beide importieren, liegt auf keiner Kante. Die
 *   Begründung verbot den Import `SplitButton → ViewHeader` — genau den
 *   vermeidet dieses Modul.
 *
 *   🔴 `secondary` fällt bewusst auf das alte Verhalten zurück
 *   (`'secondary.main'`, hell nur **2,148:1**): die v2-Skala führt keinen
 *   `secondary`-Ton, und ein neuer Token mit erfundenem Wert wäre eine eigene
 *   Story. Kein Produktionsaufrufer übergibt ihn — es verschlechtert sich also
 *   nichts.
 *
 * @useWhen
 *   - Ein Menüeintrag (oder ein Aktions-Eintrag) trägt `color`
 *   - Eine Farbe soll aus dem Theme kommen und nicht als Literal dastehen
 *
 * @dontUseFor
 *   - Eine FLÄCHE statt Text — der Zielwechsel gilt dem Text auf Papier
 *   - Ein fertiger Pfad, ein Hex oder ein CSS-weiter Wert: die gehen unverändert
 *     durch und brauchen diese Funktion nicht
 *
 * @related
 *   - SplitButton — der Aufrufer am Menü des Knopfpaares
 *   - ViewHeader — der Aufrufer am ⋯-Überlauf und an dessen Untereinträgen
 *   - v2Tone — der Auflöser, über den der Ton gelesen wird
 *
 * @reuseHints
 *   - Jede weitere Fläche, die einem Menüeintrag eine Semantikfarbe mitgibt
 *     (Kontextmenüs, Zeilen-Aktionen, Sammelaktionen)
 *   - Ein `secondary`-Ton, sobald die Skala einen führt — dann bekommt die
 *     Tabelle hier einen siebten Eintrag statt eines Sonderfalls
 */

import { v2Tone } from '../../theme/theme';

/**
 * Die sechs abgebildeten Palettenschlüssel und ihr Ton in der v2-Skala.
 *
 * 🔴 Die Paarung ist NICHT die Reihenfolge der Schlüssel: `primary` geht auf
 * `brand` (die Zeile der Skala, die die Hausfarbe als semantischen Ton führt),
 * `warning` auf `warn`, `error` auf `err`, `success` auf `ok`. Wer die zwei
 * Listen positionsweise liest, schreibt eine Rotation.
 */
const TON_JE_SCHLUESSEL = {
  warning: 'warn',
  error: 'err',
  success: 'ok',
  info: 'info',
  agent: 'agent',
  primary: 'brand',
};

/** CSS-weite Schlüssel sind gültige Werte und dürfen NIE ergänzt werden. */
const CSS_WEITE_SCHLUESSEL = new Set([
  'inherit', 'initial', 'unset', 'revert', 'revert-layer', 'currentcolor', 'transparent',
]);

/**
 * Übersetze den `color`-Wert eines Menüeintrags in einen gültigen CSS-Wert.
 *
 * 🔴 Gelesen wird der Ton über `v2Tone(theme, ton)` und nicht über
 * `theme.palette.v2.<ton>`. Das ist keine Stilfrage: `v2Tone` ist der Auflöser,
 * der `theme?.palette?.v2 ?? v2Palette(theme?.palette?.mode || 'light')` rechnet
 * — er trägt ein Theme OHNE `palette.v2` (blankes `createTheme()` in Jest, jeder
 * Aufrufer mit handgebautem Theme) und ein FEHLENDES Theme ebenso (Rückfall
 * `'light'`). Der direkte Zugriff stürzt dort ab.
 *
 * @param {string} color — der übergebene Farbwert
 * @param {object} [theme] — MUI-Theme (`useTheme()`); ohne es liefern die sechs
 *   abgebildeten Schlüssel die Hellwerte (`v2Palette('light')`), nicht sich selbst
 * @returns {string} der Ton des Schlüssels, `<schlüssel>.main`, oder der Wert
 *   unverändert
 */
export function menueFarbe(color, theme) {
  if (typeof color !== 'string' || !color) return color;
  if (color.includes('.') || color.includes('(') || color.startsWith('#')) return color;
  if (!/^[a-zA-Z]+$/.test(color) || CSS_WEITE_SCHLUESSEL.has(color.toLowerCase())) return color;
  const ton = TON_JE_SCHLUESSEL[color];
  if (ton) return v2Tone(theme, ton).main;
  return theme?.palette?.[color]?.main ? `${color}.main` : color;
}
